import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { resolveRadius } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './Skeleton.css';

export interface SkeletonProps extends BaseProps {
  visible?: boolean;
  width?: number | string;
  height?: number | string;
  circle?: boolean;
  animate?: boolean;
  children?: ReactNode;
}

/** Loading placeholder rectangle/circle with shimmer animation. */
export const Skeleton = forwardRef(function Skeleton(
  { visible = true, width, height, circle, animate = true, children, radius, ...rest }: SkeletonProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const base = useBaseAttrs('rr-skeleton', rest, [
    !visible && 'rr-skeleton--ready',
    animate && 'rr-skeleton--animate',
    circle && 'rr-skeleton--circle'
  ]);
  const { dom } = splitBaseProps(rest);
  return (
    <div {...dom}
      ref={ref}
      aria-busy={visible || undefined}
      aria-live="polite"
      {...base.attrs}
      className={base.className}
      style={{
        ...base.style,
        width: typeof width === 'number' ? `${width}px` : width ?? '100%',
        height: typeof height === 'number' ? `${height}px` : height ?? '1em',
        borderRadius: circle ? '50%' : resolveRadius(radius) ?? 'var(--rr-radius-sm)'
      }}
    >
      {!visible ? children : null}
    </div>
  );
});
